/**
 * Why this exists
 *
 * The API returns Quartz-style cron expressions: 6 fields (seconds + standard 5) or 7 fields
 * (same + optional year in the last position). The `cron-converter` library we use for
 * `stringToArray` / `getSchedule` only accepts classic Unix crontab format: exactly **five**
 * space-separated fields (minute, hour, day of month, month, day of week). It throws
 * "Invalid cron string format" for any other field count.
 *
 * This helper bridges that gap: it normalizes server crons into the 5-field string the library
 * expects—dropping the leading seconds field when present, dropping an optional trailing year,
 * and replacing `?` with `*` where Quartz uses `?` (not supported by `cron-converter` as-is).
 *
 * Trade-off: removing the year field means the 5-field expression no longer encodes Quartz’s
 * year constraint; callers that need strict year semantics must combine this with schedule
 * metadata (e.g. `endAt`) or another approach.
 *
 * @param {string} cronString - Raw cron from the server (Quartz-style, 5–7 fields after split).
 * @returns {string} A 5-field cron string compatible with `cron-converter`.
 */
export const normalizeServerCronForCronConverter = cronString => {
  if (typeof cronString !== 'string' || !cronString.trim()) {
    throw new Error('Invalid cron string');
  }

  const parts = cronString
    .replace(/\?/g, '*')
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (parts.length === 5) {
    return parts.join(' ');
  }

  if (parts.length === 7) {
    return parts.slice(1, 6).join(' ');
  }

  if (parts.length === 6) {
    const last = parts[parts.length - 1];
    const looksLikeYear = /^\d{4}$/.test(last);
    if (looksLikeYear) {
      return parts.slice(0, 5).join(' ');
    }
    return parts.slice(1, 6).join(' ');
  }

  throw new Error('Invalid cron string format');
};
